import type { FC, ReactElement } from 'react';
import { Fragment, useRef } from 'react';
import { Badge, Button, Col, Dropdown, Input, Menu, Popover, Row, Space, Switch, Typography } from 'antd';
import type { InputRef } from 'antd';
import { ListFilter } from 'lucide-react';
import {
    TbAdjustmentsHorizontal,
    TbCalendarEvent,
    TbCircleXFilled,
    TbCopy,
    TbDatabaseMinus,
    TbFilePlus,
    TbFileText,
    TbNote,
    TbQuote,
    TbSearch,
    TbSlash,
    TbTopologyStar3,
} from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import ContextualCommentButton from '@/shared/components/activity/ContextualCommentButton';
import DocumentConceptSider from '@/shared/components/attachments/AttachmentRedactionEditor/DocumentConceptSider';
import type {
    DocumentConceptReference,
    DocumentIntelligence,
} from '@/shared/components/attachments/AttachmentRedactionEditor/documentIntelligence';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import { PlatformSelect as Select } from '@/shared/components/forms';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import TabbedResourceSider from '@/shared/components/layouts/siders/TabbedResourceSider';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import type { ContextualCommentResource, ContextualCommentTarget, Memo } from '@/types';
import { dayjs } from '@/utilities';

import type {
    TranscriptDetailsTab,
    TranscriptOrdering,
    TranscriptPeriod,
    TranscriptPeriodFilter,
    TranscriptQuoteSelection,
} from './types';
import { getTranscriptLabel } from './utils';

const TRANSCRIPT_SIDER_WIDTH = '24rem';
const TRANSCRIPT_DETAILS_WIDTH = '22rem';

interface TranscriptListSiderProps {
    activePeriod?: TranscriptPeriod;
    emptyDescription: string;
    filter: TranscriptPeriodFilter;
    loading: boolean;
    ordering: TranscriptOrdering;
    showDates: boolean;
    onFilterChange: (filter: TranscriptPeriodFilter) => void;
    onOrderingChange: (ordering: TranscriptOrdering) => void;
    onQueryChange: (query: string) => void;
    onSelectPeriod: (period: TranscriptPeriod) => void;
    onShowDatesChange: (showDates: boolean) => void;
    open: boolean;
    periods: TranscriptPeriod[];
    query: string;
}

export const TranscriptListSider: FC<TranscriptListSiderProps> = ({
    activePeriod,
    emptyDescription,
    filter,
    loading,
    onFilterChange,
    onOrderingChange,
    onQueryChange,
    onSelectPeriod,
    onShowDatesChange,
    open,
    ordering,
    periods,
    query,
    showDates,
}) => {
    const { token } = useAntDesignTheme();
    const searchRef = useRef<InputRef>(null);

    useSlashSearchFocus(searchRef, open, { priority: 30 });

    const filterOptions: Array<{
        description: string;
        label: string;
        value: TranscriptPeriodFilter;
    }> = [
        { description: 'Show earnings calls from every fiscal quarter.', label: 'All earnings calls', value: 'all' },
        { description: 'Show first-quarter earnings calls.', label: 'First quarter', value: 'q1' },
        { description: 'Show second-quarter earnings calls.', label: 'Second quarter', value: 'q2' },
        { description: 'Show third-quarter earnings calls.', label: 'Third quarter', value: 'q3' },
        { description: 'Show fourth-quarter earnings calls.', label: 'Fourth quarter', value: 'q4' },
    ];
    const filterDropdownContent = (
        <DropdownPanel
            header={<DropdownMenuLabel icon={<ListFilter size='0.75rem' />}>Filter</DropdownMenuLabel>}
            width='20rem'
        >
            <DropdownPanelMenu selectable={false} style={{ paddingBlock: '0.25rem' }}>
                {filterOptions.map((option) => (
                    <Menu.Item key={option.value} onClick={() => onFilterChange(option.value)}>
                        <DropdownOptionContent
                            description={option.description}
                            icon={<TbCalendarEvent size='0.875rem' />}
                            selected={filter === option.value}
                            title={option.label}
                        />
                    </Menu.Item>
                ))}
            </DropdownPanelMenu>
        </DropdownPanel>
    );
    const displayPopoverContent = (
        <div style={{ width: '21rem' }}>
            <div style={{ padding: '0.875rem 1rem' }}>
                <Row align='middle' gutter={12} wrap={false}>
                    <Col flex='auto'>
                        <Typography.Text style={{ color: token.colorTextTertiary }}>Ordering</Typography.Text>
                    </Col>
                    <Col flex='none'>
                        <Select
                            options={[
                                { label: 'Newest', value: 'newest' },
                                { label: 'Oldest', value: 'oldest' },
                            ]}
                            popupMatchSelectWidth={false}
                            size='small'
                            style={{ width: '7rem' }}
                            value={ordering}
                            onChange={(value) => onOrderingChange(value as TranscriptOrdering)}
                        />
                    </Col>
                </Row>
            </div>
            <div style={{ borderTop: `1px solid ${token.colorBorder}`, padding: '0.75rem 1rem' }}>
                <Row align='middle' gutter={12} wrap={false}>
                    <Col flex='auto'>
                        <Typography.Text style={{ color: token.colorTextTertiary }}>Show call dates</Typography.Text>
                    </Col>
                    <Col flex='none'>
                        <Switch checked={showDates} onChange={onShowDatesChange} />
                    </Col>
                </Row>
            </div>
        </div>
    );

    return (
        <Col
            aria-hidden={!open}
            flex={open ? `0 0 ${TRANSCRIPT_SIDER_WIDTH}` : '0 0 0'}
            style={{
                borderRight: open ? `1px solid ${token.colorBorder}` : 'none',
                maxWidth: open ? TRANSCRIPT_SIDER_WIDTH : 0,
                minWidth: open ? '20rem' : 0,
                opacity: open ? 1 : 0,
                overflow: 'hidden',
                pointerEvents: open ? 'auto' : 'none',
                transition: [
                    `flex-basis ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    `max-width ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    `min-width ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    `opacity ${token.motionDurationFast} ${token.motionEaseInOut}`,
                ].join(', '),
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: TRANSCRIPT_SIDER_WIDTH }}>
                <div style={{ padding: '1rem 1rem 0.75rem' }}>
                    <Row align='middle' gutter={8} wrap={false}>
                        <Col flex='auto' style={{ minWidth: 0 }}>
                            <Typography.Title level={4} style={{ marginBottom: 0 }}>
                                Transcripts
                            </Typography.Title>
                        </Col>
                        <Col flex='none'>
                            <Space.Compact>
                                <Dropdown
                                    popupRender={() => filterDropdownContent}
                                    placement='bottomRight'
                                    trigger={['click']}
                                >
                                    <Button
                                        aria-label='Filter transcripts'
                                        icon={
                                            <Badge
                                                dot
                                                color='blue'
                                                offset={[-1, 1]}
                                                style={{ display: filter === 'all' ? 'none' : undefined }}
                                            >
                                                <ListFilter
                                                    size='0.875rem'
                                                    style={{ color: token.colorTextTertiary }}
                                                />
                                            </Badge>
                                        }
                                        size='small'
                                        title='Filter transcripts'
                                    />
                                </Dropdown>
                                <Popover
                                    arrow={false}
                                    content={displayPopoverContent}
                                    placement='bottomRight'
                                    styles={{
                                        body: {
                                            border: `1px solid ${token.colorBorder}`,
                                            borderRadius: token.borderRadius,
                                            padding: 0,
                                        },
                                    }}
                                    trigger='click'
                                >
                                    <Button
                                        aria-label='Transcript display options'
                                        icon={<TbAdjustmentsHorizontal style={{ color: token.colorTextTertiary }} />}
                                        size='small'
                                        title='Transcript display options'
                                    />
                                </Popover>
                            </Space.Compact>
                        </Col>
                    </Row>
                </div>
                <div style={{ borderBottom: `1px solid ${token.colorBorder}`, padding: '0 1rem 1rem' }}>
                    <Input
                        ref={searchRef}
                        allowClear={{
                            clearIcon: <TbCircleXFilled size='1rem' style={{ color: token.colorTextSecondary }} />,
                        }}
                        aria-label='Search transcripts'
                        placeholder='Search Transcripts...'
                        prefix={<TbSearch size='1rem' style={{ color: token.colorTextQuaternary }} />}
                        size='middle'
                        suffix={<TbSlash size='1rem' style={{ color: token.colorTextQuaternary }} />}
                        value={query}
                        onChange={(event) => onQueryChange(event.target.value)}
                    />
                </div>
                <OverflowFadeScrollArea
                    aria-label='Earnings call transcripts'
                    containerStyle={{ flex: '1 1 auto', minHeight: 0 }}
                    contentStyle={{ height: !loading && periods.length === 0 ? '100%' : undefined }}
                    surface='container'
                >
                    {loading ? (
                        <div style={{ padding: '1rem' }}>
                            <BaseSkeleton active paragraph={{ rows: 14 }} title={false} />
                        </div>
                    ) : periods.length ? (
                        <DropdownPanelMenu selectable={false} style={{ paddingBlock: 0 }}>
                            {periods.flatMap((period, index) => {
                                const key = `${period.year}-q${period.quarter}`;
                                const selected =
                                    period.quarter === activePeriod?.quarter && period.year === activePeriod?.year;
                                const dateLabel =
                                    period.date && dayjs(period.date).isValid()
                                        ? dayjs(period.date).format('MMM D, YYYY')
                                        : 'Earnings call';

                                return [
                                    <Menu.Item
                                        aria-current={selected ? 'true' : undefined}
                                        key={key}
                                        onClick={() => onSelectPeriod(period)}
                                        style={{
                                            background: selected ? token.colorFillSecondary : undefined,
                                            border: 0,
                                            borderRadius: 0,
                                            height: 'auto',
                                            lineHeight: 'normal',
                                            margin: 0,
                                            padding: '0.75rem',
                                        }}
                                    >
                                        <DropdownOptionContent
                                            avatar={{
                                                icon: <TbFileText size='0.875rem' />,
                                                style: { border: `1px dashed ${token.colorBorder}` },
                                            }}
                                            description={showDates ? dateLabel : undefined}
                                            singleLine
                                            title={getTranscriptLabel(period)}
                                        />
                                    </Menu.Item>,
                                    ...(index < periods.length - 1
                                        ? [
                                              <Menu.Divider
                                                  key={`${key}:divider`}
                                                  style={{ borderColor: token.colorBorder, margin: 0 }}
                                              />,
                                          ]
                                        : []),
                                ];
                            })}
                        </DropdownPanelMenu>
                    ) : (
                        <EmptySection
                            description={
                                query || filter !== 'all'
                                    ? 'No earnings calls match the current search and filters.'
                                    : emptyDescription
                            }
                            height='100%'
                            icon={<TbDatabaseMinus size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                            label={query || filter !== 'all' ? 'No Matching Transcripts' : 'No Transcript Data'}
                        />
                    )}
                </OverflowFadeScrollArea>
                {/* Audio controls intentionally deferred until transcript audio sources are introduced. */}
            </div>
        </Col>
    );
};

interface TranscriptDetailsSiderProps {
    activeOntologyReferenceId?: string;
    linkedMemos: Memo[];
    ontologyEnabled: boolean;
    ontologyError?: string;
    ontologyIntelligence: DocumentIntelligence;
    ontologyLoading: boolean;
    ontologyReferenceLocationLabel: (reference: DocumentConceptReference) => string;
    onOntologyReferenceSelect: (reference: DocumentConceptReference) => void;
    onTabChange: (tab: TranscriptDetailsTab) => void;
    open: boolean;
    selection?: TranscriptQuoteSelection;
    tab: TranscriptDetailsTab;
}

export const TranscriptDetailsSider: FC<TranscriptDetailsSiderProps> = ({
    activeOntologyReferenceId,
    linkedMemos,
    ontologyEnabled,
    ontologyError,
    ontologyIntelligence,
    ontologyLoading,
    ontologyReferenceLocationLabel,
    onOntologyReferenceSelect,
    onTabChange,
    open,
    selection,
    tab,
}) => {
    const { token } = useAntDesignTheme();
    const navigate = useNavigate();
    const detailsContent = {
        highlights: selection ? (
            <div style={{ padding: '1rem' }}>
                <Typography.Text strong>{selection.speaker}</Typography.Text>
                <Typography.Paragraph
                    ellipsis={{ rows: 10, expandable: true, symbol: 'See more' }}
                    style={{
                        background: token.colorFillQuaternary,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: token.borderRadius,
                        margin: '0.75rem 0 0',
                        padding: '0.75rem',
                    }}
                >
                    {selection.quote}
                </Typography.Paragraph>
            </div>
        ) : (
            <EmptySection
                description='Select transcript text to quote, copy, link to a Memo, or comment.'
                height='100%'
                icon={<TbQuote size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                label='No Highlight Selected'
            />
        ),
        references: linkedMemos.length ? (
            <DropdownPanelMenu selectable={false} style={{ padding: '0.5rem 0' }}>
                {linkedMemos.map((memo, index) => (
                    <Fragment key={memo.id}>
                        {index ? <Menu.Divider /> : null}
                        <Menu.Item key={memo.id} onClick={() => navigate(`/memos/${memo.id}`)}>
                            <DropdownOptionContent
                                avatar={{ icon: <TbNote size='0.875rem' /> }}
                                description='Includes a quote from this transcript'
                                singleLine
                                title={memo.title || 'Untitled Memo'}
                            />
                        </Menu.Item>
                    </Fragment>
                ))}
            </DropdownPanelMenu>
        ) : (
            <EmptySection
                description='Memos linked to this earnings call will appear here.'
                height='100%'
                icon={<TbNote size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                label='No Linked Memos'
            />
        ),
        ontology: !ontologyEnabled ? (
            <EmptySection
                description='Enable Ontology Mode from the transcript toolbar to identify taxonomy-backed concepts.'
                height='100%'
                icon={<TbTopologyStar3 size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                label='Ontology Mode Off'
            />
        ) : ontologyError ? (
            <EmptySection
                description={ontologyError}
                height='100%'
                icon={<TbTopologyStar3 size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                label='Ontology Unavailable'
            />
        ) : (
            <DocumentConceptSider
                activeReferenceId={activeOntologyReferenceId}
                intelligence={ontologyIntelligence}
                loading={ontologyLoading}
                referenceLocationLabel={ontologyReferenceLocationLabel}
                textExtractionSupported
                onReferenceSelect={onOntologyReferenceSelect}
            />
        ),
    };

    return (
        <Col
            aria-hidden={!open}
            flex={open ? `0 0 ${TRANSCRIPT_DETAILS_WIDTH}` : '0 0 0'}
            style={{
                borderLeft: open ? `1px solid ${token.colorBorder}` : 'none',
                maxWidth: open ? TRANSCRIPT_DETAILS_WIDTH : 0,
                minWidth: open ? '20rem' : 0,
                opacity: open ? 1 : 0,
                overflow: 'hidden',
                pointerEvents: open ? 'auto' : 'none',
                transition: [
                    `flex-basis ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    `max-width ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    `min-width ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    `opacity ${token.motionDurationFast} ${token.motionEaseInOut}`,
                ].join(', '),
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    width: TRANSCRIPT_DETAILS_WIDTH,
                }}
            >
                <TabbedResourceSider
                    activeKey={tab}
                    items={[
                        {
                            children: (
                                <OverflowFadeScrollArea
                                    aria-label='Transcript highlights'
                                    containerStyle={{ height: '100%' }}
                                    contentStyle={{ height: '100%' }}
                                    surface='container'
                                >
                                    {detailsContent.highlights}
                                </OverflowFadeScrollArea>
                            ),
                            key: 'highlights',
                            label: 'Highlights',
                        },
                        {
                            children: (
                                <OverflowFadeScrollArea
                                    aria-label='Transcript references'
                                    containerStyle={{ height: '100%' }}
                                    contentStyle={{ height: '100%' }}
                                    surface='container'
                                >
                                    {detailsContent.references}
                                </OverflowFadeScrollArea>
                            ),
                            key: 'references',
                            label: `References${linkedMemos.length ? ` (${linkedMemos.length})` : ''}`,
                        },
                        {
                            children: detailsContent.ontology,
                            icon: <TbTopologyStar3 size='0.875rem' />,
                            key: 'ontology',
                            label: 'Ontology',
                        },
                    ]}
                    onChange={(key) => onTabChange(key as TranscriptDetailsTab)}
                    size='small'
                />
            </div>
        </Col>
    );
};

interface TranscriptSelectionToolbarProps {
    commentResource?: ContextualCommentResource;
    commentTarget?: ContextualCommentTarget;
    memoPanel: ReactElement;
    onCopy: () => void;
    onCreateMemo: () => void;
    selection: TranscriptQuoteSelection;
}

export const TranscriptSelectionToolbar: FC<TranscriptSelectionToolbarProps> = ({
    commentResource,
    commentTarget,
    memoPanel,
    onCopy,
    onCreateMemo,
    selection,
}) => {
    const { token } = useAntDesignTheme();

    return (
        <div
            data-selection-control='true'
            style={{
                background: token.colorBgElevated,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                left: selection.left,
                padding: '0.25rem',
                position: 'absolute',
                top: selection.top,
                zIndex: token.zIndexPopupBase,
            }}
        >
            <Space size={4}>
                <Button icon={<TbFilePlus />} onClick={onCreateMemo} size='small' type='primary'>
                    New Memo
                </Button>
                <Dropdown popupRender={() => memoPanel} trigger={['click']}>
                    <DropdownButton size='small'>Add to Memo</DropdownButton>
                </Dropdown>
                <Button aria-label='Copy transcript quote' icon={<TbCopy />} onClick={onCopy} size='small' />
                {commentResource && commentTarget ? (
                    <ContextualCommentButton resource={commentResource} target={commentTarget} />
                ) : null}
            </Space>
        </div>
    );
};
